/**
 * One sanitiser for every exported file name and quote-number value
 * (CONF-208, CONF-214).
 *
 * The configurator serial comes back from the getNextQuoteSerial Lambda as
 * `#<n>`, and the Salesforce number and customer name are free text. A `#`
 * (or `&`, `?`, `%`, `/`, …) in a file name breaks downstream systems that
 * treat it as a URL or key — the S3 quote-explorer copy, email attachments,
 * Windows paths — so every dynamic segment is reduced to `[A-Za-z0-9_-]`.
 * The fixed parts of each name (prefixes, `.pdf`, ISO timestamps) are
 * already safe and are not passed through here.
 */

// Whitespace survives this pass so runs of it can collapse to one '_' below.
const UNSAFE_CHAR = /[^A-Za-z0-9_\s-]/g;

/**
 * Longest a free-text segment (customer name, Salesforce number, serial,
 * quarter) may be. With every builder's fixed parts added, no export name can
 * exceed MAX_S3_KEY_FILENAME, so the S3 key below is always the downloaded
 * name itself (CONF-242): the longest shape is the BOM CSV,
 * 13 + 36 + 1 + 36 + 5 + 24 + 4 = 119.
 */
export const MAX_FILENAME_SEGMENT = 36;

export function sanitizeFilenamePart(value, fallback = 'NA') {
  // Strip the specials first, then turn whatever whitespace is left into a
  // single '_' — so 'SF #99 / draft' becomes 'SF_99_draft', not 'SF_99__draft'.
  const cleaned = String(value ?? '')
    .replace(UNSAFE_CHAR, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, MAX_FILENAME_SEGMENT);
  return cleaned || fallback;
}

/**
 * The BOM CSV "Quote Number" cell: `<serial>-<salesforce>` with the serial's
 * `#` (and any other special character) removed, so the bare serial reaches
 * downstream processing. Unlike a file name, a missing half stays empty
 * rather than becoming a placeholder — the cell is data, not a path.
 */
export function buildCsvQuoteNumber(config = {}) {
  const serial = sanitizeFilenamePart(config.configuratorQuoteSerial, '');
  const salesforce = sanitizeFilenamePart(config.salesforceQuoteNumber, '');
  return `${serial}-${salesforce}`;
}

/**
 * The quote-explorer S3 object key (CONF-242, ASVS 5.4.2). Every name the
 * builders produce (buildBomCsvFilename, buildVduraQuoteFilename,
 * buildCciFilename, the PPTX name) is already inside AWS's safe-character set
 * and, through MAX_FILENAME_SEGMENT, under 120 characters, so for them this
 * is the identity — which matters, because the quote-explorer consumer
 * matches BOM files by basename and files PDFs by the serial in the name
 * (CONF-236 evidence). It only changes a name that did not come from a
 * builder: each character outside the safe set becomes '_' (runs are NOT
 * collapsed — '_' runs are key-safe and a builder emits them for free text
 * such as 'Acme_'), a leading '.' or path separator is stripped so the key
 * cannot climb out of the NewQuotes/ prefix, and the whole name is capped at
 * 120 characters with the extension kept. exportFilename.test.js pins the
 * identity over real builder output.
 */
export const MAX_S3_KEY_FILENAME = 120;

const UNSAFE_KEY_CHAR = /[^A-Za-z0-9!_.*'()-]/g;

export function sanitizeS3ObjectKey(filename) {
  let name = String(filename ?? '')
    .replace(UNSAFE_KEY_CHAR, '_')
    .replace(/^[._]+/, '');
  if (!name) return 'file';
  if (name.length > MAX_S3_KEY_FILENAME) {
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 ? name.slice(dot) : '';
    name = name.slice(0, MAX_S3_KEY_FILENAME - ext.length) + ext;
  }
  return name;
}
